import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  fetchPriceStatisticsState,
  runPriceStatistics,
  type PriceStatisticsState,
} from './priceStatisticsApi';

/**
 * Statystyka cen z VIA (13.10.2026, przełącznik `priceStatistics`): skąd
 * liczby, ostatnie przeliczenie, harmonogram i „Przelicz teraz” (sekcja
 * administracyjna — to samo `canEdit` co zmiana parametrów; rozstrzyga API).
 * Próg wiarygodności to parametr na liście wyżej.
 */

export const PRICE_STATISTICS_NOTE =
  'Cena klienta za km to stawka zlecenia z TMS (cena od klienta, przychód) podzielona przez dystans trasy z TMS, ' +
  'w EUR — inna waluta kursem średnim NBP z dnia załadunku. Statystyka obejmuje wszystkie zlecenia z TMS ' +
  'z załadunkiem do dnia przeliczenia (bez danych pokazowych, czynności wózkowych, nieaktualnych bez realizacji ' +
  'i podejrzanych dat). Przy zleceniu: mediana relacji, klienta i spółki; poziom poniżej progu — „za mało danych”.';

export const PriceStatisticsSection = ({ canEdit }: { canEdit: boolean | null }) => {
  const notify = useNotify();
  const [state, setState] = useState<PriceStatisticsState | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await fetchPriceStatisticsState());
    } catch {
      // Stan nie jest konieczny do pracy z parametrami — sekcja zostaje bez liczb.
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await runPriceStatistics();
      notify(`Przeliczono statystykę cen: ${r.used} z ${r.examined} zleceń, ${r.rows} wierszy`, {
        type: 'success',
      });
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const last = state?.runs[0] ?? null;
  return (
    <Box sx={{ mt: 4 }} data-testid="price-statistics-section">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Statystyka cen z VIA
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {PRICE_STATISTICS_NOTE}
      </Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} data-testid="price-statistics-status">
          {last
            ? `Ostatnie przeliczenie: ${fmtDate(last.startedAt)}${
                last.error
                  ? ` — błąd: ${last.error}`
                  : ` — ${last.used} z ${last.examined} zleceń, ${last.rows} wierszy`
              }. `
            : 'Statystyki jeszcze nie przeliczono. '}
          {state
            ? state.enabled
              ? `Przeliczenie co noc o ${String(state.hour).padStart(2, '0')}:00.`
              : 'Przeliczenie nocne wyłączone (STATYSTYKA_CEN).'
            : ''}
          {state && state.errors.length > 0 ? ` ${state.errors.join(' ')}` : ''}
        </Typography>
        {canEdit === true && (
          <Button variant="outlined" size="small" onClick={run} disabled={busy || state?.running === true}>
            {busy ? 'Przeliczanie…' : 'Przelicz teraz'}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
